/**
 * Betreuungsakten - Liste und Verwaltung
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTabSearchParams } from '@/hooks/useTabRoute';
import {
  Users,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  RefreshCw,
  X,
  AlertCircle,
  Clock,
  CheckCircle2,
  UserCheck,
  Sparkles,
  Star,
  ArrowRightLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
// Dialog imports removed - all forms are now inline
import { betreuungAPI } from '@/api/betreuungAPI';
import { useAuth } from '@/contexts/AuthContext';
import { useTabs } from '@/contexts/TabContext';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: 'alle', label: 'Alle Status' },
  { value: 'aktiv', label: 'Aktiv' },
  { value: 'ruhend', label: 'Ruhend' },
  { value: 'uebergabe', label: 'Übergabe' },
  { value: 'beendet', label: 'Beendet' }
];

const PRIORITAET_OPTIONS = [
  { value: 'alle', label: 'Alle Prioritäten' },
  { value: 'dringend', label: 'Dringend' },
  { value: 'hoch', label: 'Hoch' },
  { value: 'mittel', label: 'Mittel' },
  { value: 'niedrig', label: 'Niedrig' }
];

const SORT_OPTIONS = [
  { value: 'nachname_asc', label: 'Nachname A-Z' },
  { value: 'nachname_desc', label: 'Nachname Z-A' },
  { value: 'vorname_asc', label: 'Vorname A-Z' },
  { value: 'vorname_desc', label: 'Vorname Z-A' },
  { value: 'fallnummer_asc', label: 'Aktenzeichen aufsteigend' },
  { value: 'fallnummer_desc', label: 'Aktenzeichen absteigend' },
  { value: 'created_desc', label: 'Neueste zuerst' },
  { value: 'created_asc', label: 'Älteste zuerst' },
];

const STATUS_COLORS = {
  aktiv: 'bg-green-100 text-green-700 border-green-200',
  ruhend: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  uebergabe: 'bg-orange-100 text-orange-700 border-orange-200',
  beendet: 'bg-slate-100 text-slate-600 border-slate-200'
};

const PRIORITAET_COLORS = {
  niedrig: 'bg-slate-100 text-slate-700',
  mittel: 'bg-blue-100 text-blue-700',
  hoch: 'bg-orange-100 text-orange-700',
  dringend: 'bg-red-100 text-red-700'
};

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function KlientenPage() {
  const [searchParams, setSearchParams] = useTabSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openTab } = useTabs();
  
  // Sachbearbeiter: nur Akten mit zugewiesenen Aufgaben
  const isSachbearbeiter = user?.rolle === 'sachbearbeiter';
  
  const [akten, setAkten] = useState([]);
  const [meineKlientIds, setMeineKlientIds] = useState([]);
  const [favoriten, setFavoriten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ladeFehler, setLadeFehler] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'alle');
  const [prioritaetFilter, setPrioritaetFilter] = useState(searchParams.get('prioritaet') || 'alle');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'nachname_asc');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, akte: null });

  // Favoriten laden
  const loadFavoriten = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/favoriten/klienten`);
      if (res.ok) {
        const data = await res.json();
        setFavoriten(data || []);
      }
    } catch (error) {
      console.error('Favoriten laden Fehler:', error);
    }
  }, []);

  // Favorit hinzufügen/entfernen
  const toggleFavorit = async (klientId, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    const isFav = favoriten.includes(klientId);
    try {
      if (isFav) {
        await fetch(`${API_URL}/api/favoriten/klienten/${klientId}`, { method: 'DELETE' });
        setFavoriten(prev => prev.filter(id => id !== klientId));
        toast.success('Von Favoriten entfernt');
      } else {
        await fetch(`${API_URL}/api/favoriten/klienten/${klientId}`, { method: 'POST' });
        setFavoriten(prev => [...prev, klientId]);
        toast.success('Zu Favoriten hinzugefügt');
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren der Favoriten');
    }
  };

  useEffect(() => {
    loadFavoriten();
  }, [loadFavoriten]);

  // Sortier-Funktion
  const sortAkten = (data) => {
    return [...data].sort((a, b) => {
      switch (sortBy) {
        case 'nachname_asc':
          return (a.nachname || '').localeCompare(b.nachname || '', 'de');
        case 'nachname_desc':
          return (b.nachname || '').localeCompare(a.nachname || '', 'de');
        case 'vorname_asc':
          return (a.vorname || '').localeCompare(b.vorname || '', 'de');
        case 'vorname_desc':
          return (b.vorname || '').localeCompare(a.vorname || '', 'de');
        case 'fallnummer_asc':
          return (a.fallnummer || '').localeCompare(b.fallnummer || '', 'de', { numeric: true });
        case 'fallnummer_desc':
          return (b.fallnummer || '').localeCompare(a.fallnummer || '', 'de', { numeric: true });
        case 'created_desc':
          return (b.created_date || '').localeCompare(a.created_date || '');
        case 'created_asc':
          return (a.created_date || '').localeCompare(b.created_date || '');
        default:
          return (a.nachname || '').localeCompare(b.nachname || '', 'de');
      }
    });
  };

  const loadAkten = async () => {
    setLoading(true);
    setLadeFehler(false);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'alle') {
        params.status = statusFilter;
      }
      if (prioritaetFilter && prioritaetFilter !== 'alle') {
        params.prioritaet = prioritaetFilter;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      // Parallel laden: Klienten und (für Sachbearbeiter) Aufgaben
      const promises = [betreuungAPI.listKlienten(params)];
      if (isSachbearbeiter && user?.id) {
        promises.push(betreuungAPI.listAufgaben({}));
      }
      
      const results = await Promise.all(promises);
      let result = Array.isArray(results[0]) ? results[0] : [];
      
      // Sachbearbeiter: Nur Akten mit zugewiesenen Aufgaben anzeigen
      if (isSachbearbeiter && user?.id && results[1]) {
        const aufgaben = results[1];
        const meineAufgaben = aufgaben.filter(a => a.zugewiesen_an_id === user.id);
        const klientIds = [...new Set(meineAufgaben.map(a => a.klient_id).filter(Boolean))];
        setMeineKlientIds(klientIds);
        result = result.filter(a => klientIds.includes(a.id));
      }
      
      setAkten(sortAkten(result));
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      toast.error('Fehler beim Laden der Betreuungsakten');
      // Fehlerzustand separat markieren -- die vorhandene Akten-Liste NICHT
      // ueberschreiben, damit ein fehlgeschlagener Request nie faelschlich
      // als "0 Akten vorhanden" interpretiert wird (UX-Fund 26.09.2026).
      setLadeFehler(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAkten();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, prioritaetFilter, sortBy]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadAkten();
    }, 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    if (value === 'alle') {
      searchParams.delete('status');
    } else {
      searchParams.set('status', value);
    }
    setSearchParams(searchParams);
  };

  const handlePrioritaetChange = (value) => {
    setPrioritaetFilter(value);
    if (value === 'alle') {
      searchParams.delete('prioritaet');
    } else {
      searchParams.set('prioritaet', value);
    }
    setSearchParams(searchParams);
  };

  const handleDelete = async () => {
    if (!deleteDialog.akte) return;
    
    try {
      await betreuungAPI.deleteKlient(deleteDialog.akte.id);
      toast.success('Betreuungsakte wurde gelöscht');
      setDeleteDialog({ open: false, akte: null });
      loadAkten();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  return (
    <div className="space-y-6" data-testid="akten-page">
      {/* Info-Banner für Sachbearbeiter */}
      {isSachbearbeiter && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <UserCheck className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-800">Ihre zugewiesenen Betreuungsakten</p>
            <p className="text-sm text-blue-600 mt-1">
              Als Sachbearbeiter sehen Sie nur Betreuungsakten, für die Sie zugewiesene Aufgaben haben.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Betreuungsakten</h1>
          <p className="text-slate-500 mt-1">
            {akten.length} {akten.length === 1 ? 'Akte' : 'Akten'}
          </p>
        </div>
        {/* Nur Admin und Betreuer können neue Akten erstellen */}
        {!isSachbearbeiter && (
          <Link to="/akten/neu">
            <Button data-testid="new-akte-btn">
              <Plus className="w-4 h-4 mr-2" />
              Neue Betreuungsakte
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Suche nach Name oder Aktenzeichen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
            </button>
          )}
        </div>
        <Select value={statusFilter} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={prioritaetFilter} onValueChange={handlePrioritaetChange}>
          <SelectTrigger className="w-full sm:w-40" data-testid="prioritaet-filter">
            <SelectValue placeholder="Priorität" />
          </SelectTrigger>
          <SelectContent>
            {PRIORITAET_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={(v) => { setSortBy(v); searchParams.set('sort', v); setSearchParams(searchParams); }}>
          <SelectTrigger className="w-full sm:w-48" data-testid="sort-select">
            <SelectValue placeholder="Sortierung" />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={loadAkten} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : ladeFehler ? (
          <div className="text-center py-12" data-testid="akten-lade-fehler">
            <AlertCircle className="w-16 h-16 mx-auto text-red-300 mb-4" />
            <p className="text-slate-700 font-medium mb-1">Betreuungsakten konnten nicht geladen werden</p>
            <p className="text-slate-500 text-sm mb-4">Der Server war nicht erreichbar. Das bedeutet nicht, dass keine Akten vorhanden sind.</p>
            <Button variant="outline" onClick={loadAkten}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Erneut versuchen
            </Button>
          </div>
        ) : akten.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {akten.map((akte) => (
              <div
                key={akte.id}
                className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                data-testid={`akte-row-${akte.id}`}
              >
                <Link
                  to={`/akten/${akte.id}`}
                  className="flex items-center gap-4 flex-1 min-w-0"
                >
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-semibold text-lg">
                      {akte.vorname?.[0]}{akte.nachname?.[0]}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 truncate">
                      {akte.vorname} {akte.nachname}
                    </p>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {akte.fallnummer && (
                        <span className="text-sm text-slate-500">
                          Az: {akte.fallnummer}
                        </span>
                      )}
                      {akte.geburtsdatum && (
                        <span className="text-sm text-slate-500">
                          * {new Date(akte.geburtsdatum).toLocaleDateString('de-DE')}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
                <div className="flex items-center gap-3">
                  {/* Favoriten-Stern */}
                  <button
                    onClick={(e) => toggleFavorit(akte.id, e)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      favoriten.includes(akte.id) 
                        ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50' 
                        : 'text-slate-300 hover:text-amber-500 hover:bg-amber-50'
                    }`}
                    title={favoriten.includes(akte.id) ? 'Von Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
                    data-testid={`favorit-toggle-${akte.id}`}
                  >
                    <Star className={`w-5 h-5 ${favoriten.includes(akte.id) ? 'fill-amber-500' : ''}`} />
                  </button>
                  {akte.prioritaet && akte.prioritaet !== 'mittel' && (
                    <span className={`px-2 py-1 rounded text-xs font-medium ${PRIORITAET_COLORS[akte.prioritaet] || ''}`}>
                      {akte.prioritaet}
                    </span>
                  )}
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[akte.status] || STATUS_COLORS.aktiv}`}>
                    {akte.status === 'uebergabe' ? 'Übergabe' : akte.status || 'aktiv'}
                  </span>
                  {/* Akte auf einen Blick Button */}
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { e.preventDefault(); openTab(`/akten/${akte.id}/blick`, { title: `${akte.vorname} ${akte.nachname} - Blick` }); navigate(`/akten/${akte.id}/blick`); }}
                    title="Akte auf einen Blick"
                    data-testid={`akte-blick-btn-${akte.id}`}
                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                  >
                    <Sparkles className="w-4 h-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { openTab(`/akten/${akte.id}/blick`, { title: `${akte.vorname} ${akte.nachname} - Blick` }); navigate(`/akten/${akte.id}/blick`); }}>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Auf einen Blick
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/akten/${akte.id}`)}>
                        <Eye className="w-4 h-4 mr-2" />
                        Anzeigen
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/akten/${akte.id}/bearbeiten`)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Bearbeiten
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { openTab(`/akten/${akte.id}/uebergabe`, { title: `Übergabe: ${akte.vorname} ${akte.nachname}` }); navigate(`/akten/${akte.id}/uebergabe`); }}>
                        <ArrowRightLeft className="w-4 h-4 mr-2" />
                        Übergabe-Workflow
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={() => setDeleteDialog({ open: true, akte })}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Löschen
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">
              {searchTerm || statusFilter !== 'alle' || prioritaetFilter !== 'alle'
                ? 'Keine Akten gefunden' 
                : isSachbearbeiter 
                  ? 'Ihnen wurden noch keine Aufgaben zugewiesen'
                  : 'Noch keine Betreuungsakten vorhanden'}
            </p>
            {!searchTerm && statusFilter === 'alle' && prioritaetFilter === 'alle' && !isSachbearbeiter && (
              <Link to="/akten/neu">
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Erste Akte anlegen
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Inline Lösch-Bestätigung */}
      {deleteDialog.open && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-300 rounded-xl shadow-lg p-4 flex items-center gap-4 max-w-lg" data-testid="inline-delete-confirm">
          <p className="text-sm text-red-800">
            Akte von {deleteDialog.akte?.vorname} {deleteDialog.akte?.nachname} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => setDeleteDialog({ open: false, akte: null })}>Abbrechen</Button>
            <Button variant="destructive" size="sm" onClick={handleDelete}>Löschen</Button>
          </div>
        </div>
      )}
    </div>
  );
}
